import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Instagram, ArrowRight } from 'lucide-react';

/**
 * Footer24 — DHARA Design System
 * White bg, minimal editorial layout, green newsletter input, simple copyright.
 */
const Footer24 = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [subscribed, setSubscribed] = useState(false);

    const handleSubscribe = (e) => {
        e.preventDefault();
        if (email.trim()) {
            setSubscribed(true);
            setEmail('');
            setTimeout(() => setSubscribed(false), 4000);
        }
    };

    const shopLinks = [
        { label: 'All Pieces', path: '/template24/shop' },
        { label: 'New Arrivals', path: '/template24/shop' },
        { label: 'Saris', path: '/template24/shop' },
        { label: 'Kurtas', path: '/template24/shop' },
        { label: 'Occasion Wear', path: '/template24/shop' },
    ];

    const infoLinks = [
        { label: 'Our Story', path: '/template24/about' },
        { label: 'Journal', path: '/template24/journal' },
        { label: 'Contact', path: '/template24/contact' },
        { label: 'Shipping & Returns', path: '/template24/shipping-exchange' },
        { label: 'Privacy Policy', path: '/template24/privacy-policy' },
        { label: 'Terms', path: '/template24/terms-and-conditions' },
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400;1,500&family=Inter:wght@300;400;500;600;700&display=swap');

                .dhara-footer {
                    background: #FFFFFF;
                    border-top: 1px solid #E5E5E5;
                }
                .dhara-footer-inner {
                    max-width: 1400px;
                    margin: 0 auto;
                    padding: 80px 40px 40px;
                }
                .dhara-footer-grid {
                    display: grid;
                    grid-template-columns: 1.5fr 1fr 1fr 1.5fr;
                    gap: 60px;
                    margin-bottom: 80px;
                }
                .dhara-footer-brand-name {
                    font-family: 'Playfair Display', serif;
                    font-size: 24px;
                    font-weight: 500;
                    color: #1A1A1A;
                    margin-bottom: 20px;
                    letter-spacing: 2px;
                }
                .dhara-footer-brand-text {
                    font-family: 'Inter', sans-serif;
                    font-size: 14px;
                    color: #6B6B6B;
                    line-height: 1.8;
                    max-width: 280px;
                }
                .dhara-footer-col-title {
                    font-family: 'Inter', sans-serif;
                    font-size: 11px;
                    font-weight: 600;
                    letter-spacing: 2px;
                    text-transform: uppercase;
                    color: #1A1A1A;
                    margin-bottom: 24px;
                }
                .dhara-footer-link {
                    display: block;
                    font-family: 'Inter', sans-serif;
                    font-size: 14px;
                    color: #6B6B6B;
                    margin-bottom: 14px;
                    cursor: pointer;
                    transition: color 0.3s ease;
                    background: none;
                    border: none;
                    padding: 0;
                    text-align: left;
                }
                .dhara-footer-link:hover {
                    color: #00553A;
                }
                .dhara-newsletter-title {
                    font-family: 'Playfair Display', serif;
                    font-size: 18px;
                    font-weight: 400;
                    font-style: italic;
                    color: #00553A;
                    margin-bottom: 12px;
                }
                .dhara-newsletter-form {
                    display: flex;
                    border: 1px solid #E5E5E5;
                    margin-top: 16px;
                    transition: border-color 0.3s ease;
                }
                .dhara-newsletter-form:focus-within {
                    border-color: #00553A;
                }
                .dhara-newsletter-input {
                    flex: 1;
                    font-family: 'Inter', sans-serif;
                    font-size: 13px;
                    border: none;
                    outline: none;
                    background: transparent;
                    color: #1A1A1A;
                    padding: 12px 16px;
                }
                .dhara-newsletter-input::placeholder {
                    color: #6B6B6B;
                }
                .dhara-newsletter-btn {
                    background: #00553A;
                    border: none;
                    cursor: pointer;
                    color: #FFFFFF;
                    transition: background-color 0.3s ease;
                    padding: 12px 16px;
                    display: flex;
                    align-items: center;
                }
                .dhara-newsletter-btn:hover {
                    background-color: #006B4A;
                }
                .dhara-footer-bottom {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding-top: 24px;
                    border-top: 1px solid #E5E5E5;
                }
                .dhara-footer-copy {
                    font-family: 'Inter', sans-serif;
                    font-size: 12px;
                    color: #6B6B6B;
                }
                .dhara-footer-social {
                    display: flex;
                    gap: 20px;
                }
                .dhara-footer-social-link {
                    color: #6B6B6B;
                    transition: color 0.3s ease;
                    cursor: pointer;
                    background: none;
                    border: none;
                    padding: 0;
                }
                .dhara-footer-social-link:hover {
                    color: #00553A;
                }

                @media (max-width: 768px) {
                    .dhara-footer-inner { padding: 48px 20px 24px; }
                    .dhara-footer-grid {
                        grid-template-columns: 1fr 1fr;
                        gap: 40px;
                        margin-bottom: 48px;
                    }
                    .dhara-footer-bottom {
                        flex-direction: column;
                        gap: 16px;
                        text-align: center;
                    }
                }
                @media (max-width: 480px) {
                    .dhara-footer-grid {
                        grid-template-columns: 1fr;
                        gap: 32px;
                    }
                }
            `}</style>

            <footer className="dhara-footer">
                <div className="dhara-footer-inner">
                    <div className="dhara-footer-grid">
                        {/* Brand */}
                        <div>
                            <div className="dhara-footer-brand-name">DHARA</div>
                            <p className="dhara-footer-brand-text">
                                Rooted in craft, designed for the modern world.
                                Every piece tells a story of heritage, hand, and heart.
                            </p>
                        </div>

                        {/* Shop */}
                        <div>
                            <div className="dhara-footer-col-title">Shop</div>
                            {shopLinks.map((link) => (
                                <button
                                    key={link.label}
                                    className="dhara-footer-link"
                                    onClick={() => navigate(link.path)}
                                >
                                    {link.label}
                                </button>
                            ))}
                        </div>

                        {/* Info */}
                        <div>
                            <div className="dhara-footer-col-title">Information</div>
                            {infoLinks.map((link) => (
                                <button
                                    key={link.label}
                                    className="dhara-footer-link"
                                    onClick={() => navigate(link.path)}
                                >
                                    {link.label}
                                </button>
                            ))}
                        </div>

                        {/* Newsletter */}
                        <div>
                            <div className="dhara-newsletter-title">Subscribe to our world</div>
                            <p className="dhara-footer-brand-text" style={{ marginBottom: '20px' }}>
                                Receive stories from the studio, early access to new collections,
                                and invitations to private events.
                            </p>
                            {subscribed ? (
                                <p style={{
                                    fontFamily: "'Inter', sans-serif",
                                    fontSize: '13px',
                                    color: '#00553A',
                                    marginTop: '16px',
                                }}>
                                    Thank you for subscribing.
                                </p>
                            ) : (
                                <form className="dhara-newsletter-form" onSubmit={handleSubscribe}>
                                    <input
                                        className="dhara-newsletter-input"
                                        type="email"
                                        placeholder="Your email address"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                    />
                                    <button type="submit" className="dhara-newsletter-btn">
                                        <ArrowRight size={16} strokeWidth={1.5} />
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>

                    <div className="dhara-footer-bottom">
                        <span className="dhara-footer-copy">
                            &copy; {new Date().getFullYear()} DHARA. All rights reserved.
                        </span>
                        <div className="dhara-footer-social">
                            <button className="dhara-footer-social-link" aria-label="Instagram">
                                <Instagram size={16} strokeWidth={1.5} />
                            </button>
                            <button className="dhara-footer-social-link" aria-label="Twitter">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4 4l11.733 16H20L8.267 4z" /><path d="M4 20l6.768-6.768M15.232 10.232L20 4" />
                                </svg>
                            </button>
                            <button className="dhara-footer-social-link" aria-label="Pinterest">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 2a10 10 0 0 0-3.64 19.33c-.1-.83-.2-2.15.04-3.07l1.39-5.88s-.36-.71-.36-1.76c0-1.65.96-2.88 2.15-2.88 1.02 0 1.51.76 1.51 1.68 0 1.02-.65 2.55-.99 3.97-.28 1.19.6 2.16 1.77 2.16 2.13 0 3.77-2.25 3.77-5.48 0-2.87-2.06-4.87-5-4.87-3.41 0-5.41 2.56-5.41 5.2 0 1.03.4 2.13.89 2.73.1.12.11.22.08.34l-.33 1.36c-.05.22-.18.27-.41.16-1.54-.72-2.5-2.96-2.5-4.77 0-3.87 2.81-7.43 8.12-7.43 4.26 0 7.58 3.04 7.58 7.1 0 4.24-2.67 7.65-6.39 7.65-1.25 0-2.42-.65-2.82-1.42l-.77 2.93c-.28 1.07-1.03 2.42-1.54 3.24A10 10 0 1 0 12 2z"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </footer>
        </>
    );
};

export default Footer24;
